/**
 * Admin Pastor Page: configure who receives pastor messages; list messages.
 * Uses shared AccountPicker (same as home groups, cash tickets).
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "../../../components/layout/index.ts";
import { IconEnvelope, IconChevronRight, IconAlertTriangle } from "../../../components/Icons.tsx";
import { getTelegram, haptic } from "../../../lib/telegram.ts";
import { useAuth } from "../../../lib/auth.tsx";
import {
  graphql,
  getErrorMessage,
  withMinDelay,
  ADMIN_PASTOR_CONFIG_QUERY,
  ADMIN_PASTOR_MESSAGES_QUERY,
  ADMIN_SET_PASTOR_CONFIG_MUTATION,
  ADMIN_CLEAR_PASTOR_CONFIG_MUTATION,
} from "../../../lib/graphql.ts";
import { formatDateBangkok } from "../../../lib/bangkok-time.ts";
import { AccountPicker } from "../home-groups/components/AccountPicker.tsx";
import type { AdminAccountRef } from "../home-groups/types.ts";

interface PastorConfigResult {
  accountId: string;
  account: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    username: string | null;
    telegramId: string;
  } | null;
}

interface PastorMessageRow {
  id: string;
  accountId: string | null;
  telegramId: string | null;
  authorName: string | null;
  text: string;
  createdAt: string;
}

export function AdminPastorPage() {
  const navigate = useNavigate();
  const tg = getTelegram();
  const { account } = useAuth();
  const hasLoadedOnce = useRef(false);

  const [config, setConfig] = useState<PastorConfigResult | null>(null);
  const [messages, setMessages] = useState<PastorMessageRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSavingConfig, setIsSavingConfig] = useState(false);
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!account) return;
    setIsLoading(true);
    setError(null);
    try {
      const [configData, messagesData] = await Promise.all([
        withMinDelay(
          graphql<{ adminPastorConfig: PastorConfigResult | null }>(
            ADMIN_PASTOR_CONFIG_QUERY,
          ),
        ),
        withMinDelay(
          graphql<{ adminPastorMessages: PastorMessageRow[] }>(
            ADMIN_PASTOR_MESSAGES_QUERY,
          ),
        ),
      ]);
      setConfig(configData.adminPastorConfig ?? null);
      setMessages(messagesData.adminPastorMessages);
      hasLoadedOnce.current = true;
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, [account]);

  useEffect(() => {
    if (account) loadData();
  }, [account, loadData]);

  useEffect(() => {
    if (!tg) return;
    tg.BackButton.show();
    const handleBack = () => {
      if (selectedMessageId) {
        setSelectedMessageId(null);
      } else if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate("/admin/content");
      }
    };
    tg.BackButton.onClick(handleBack);
    return () => {
      tg.BackButton.offClick(handleBack);
      tg.BackButton.hide();
    };
  }, [tg, navigate, selectedMessageId]);

  const pastorAccountRef: AdminAccountRef | null = config?.account
    ? {
        id: config.account.id,
        firstName: config.account.firstName,
        lastName: config.account.lastName,
        username: config.account.username,
      }
    : null;

  const handlePastorChange = useCallback(
    async (acc: AdminAccountRef | null) => {
      haptic("light");
      setIsSavingConfig(true);
      setError(null);
      try {
        if (acc) {
          await graphql<{ adminSetPastorConfig: PastorConfigResult }>(
            ADMIN_SET_PASTOR_CONFIG_MUTATION,
            { accountId: acc.id },
          );
        } else {
          await graphql<{ adminClearPastorConfig: boolean }>(
            ADMIN_CLEAR_PASTOR_CONFIG_MUTATION,
          );
        }
        await loadData();
      } catch (err) {
        setError(getErrorMessage(err));
      } finally {
        setIsSavingConfig(false);
    }
    },
    [loadData],
  );

  const formatDate = (iso: string) => {
    try {
      return formatDateBangkok(iso, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  const selectedMessage = selectedMessageId
    ? messages.find((m) => m.id === selectedMessageId)
    : null;

  if (selectedMessage) {
    return (
      <Layout>
        <div className="pb-6">
          <div className="card overflow-hidden">
            <div className="p-4 border-b border-slate-100">
              <p className="text-[12px] font-medium text-slate-400 uppercase tracking-wide mb-1">
                От кого
              </p>
              <p className="text-slate-900 font-medium">
                {selectedMessage.authorName || "Гость"}
                {selectedMessage.telegramId && (
                  <span className="text-slate-500 text-sm ml-2">
                    @{selectedMessage.telegramId}
                  </span>
                )}
              </p>
              <p className="text-slate-500 text-xs mt-1">
                {formatDate(selectedMessage.createdAt)}
              </p>
            </div>
            <div className="p-4">
              <p className="text-[12px] font-medium text-slate-400 uppercase tracking-wide mb-1">
                Сообщение
              </p>
              <p className="text-slate-800 text-sm whitespace-pre-wrap">
                {selectedMessage.text}
              </p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="pb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center text-slate-600">
            <IconEnvelope className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-slate-900">
              Написать пастору
            </h1>
            <p className="text-[13px] text-slate-500">
              Настройка и сообщения
            </p>
          </div>
        </div>

        {error && (
          <div className="mb-4 flex items-center gap-2 text-amber-600 text-sm">
            <IconAlertTriangle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* Pastor config: same AccountPicker as home groups / cash tickets */}
        <div className="card overflow-hidden mb-6">
          <p className="text-[12px] font-medium text-slate-400 uppercase tracking-wide px-4 pt-4 pb-2">
            Кому приходят сообщения
          </p>
          {isLoading ? (
            <div className="px-4 pb-4 text-slate-500 text-sm">
              Загрузка…
            </div>
          ) : (
            <div className="px-4 pb-4">
              <AccountPicker
                value={pastorAccountRef}
                onChange={handlePastorChange}
                placeholder="Выберите аккаунт пастора"
                label="Аккаунт пастора"
              />
              {isSavingConfig && (
                <p className="text-slate-500 text-xs mt-2">Сохранение…</p>
              )}
            </div>
          )}
        </div>

        {/* Messages list */}
        <p className="text-[12px] font-medium text-slate-400 uppercase tracking-wide mb-2">
          Сообщения
        </p>
        {isLoading ? (
          <div className="card p-8 text-center text-slate-500 text-sm">
            Загрузка…
          </div>
        ) : messages.length === 0 ? (
          <div className="card p-8 text-center text-slate-500 text-sm">
            Нет сообщений
          </div>
        ) : (
          <div className="card overflow-hidden divide-y divide-slate-100">
            {messages.map((msg) => (
              <button
                key={msg.id}
                type="button"
                onClick={() => {
                  haptic("light");
                  setSelectedMessageId(msg.id);
                }}
                className="w-full flex items-center gap-3 min-h-[72px] px-4 py-3 active:bg-slate-50 transition-colors text-left"
              >
                <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <IconEnvelope className="w-5 h-5 text-slate-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-900 text-[15px] truncate">
                    {msg.authorName || "Гость"}
                  </p>
                  <p className="text-slate-500 text-xs truncate">
                    {msg.text.slice(0, 60)}
                    {msg.text.length > 60 ? "…" : ""}
                  </p>
                  <p className="text-slate-400 text-[11px] mt-0.5">
                    {formatDate(msg.createdAt)}
                  </p>
                </div>
                <IconChevronRight className="w-5 h-5 text-slate-300 flex-shrink-0" />
              </button>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
